import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, AlertTriangle, Leaf, ChevronRight, RefreshCw, 
  Thermometer, Activity, ArrowLeft, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Results() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [userKit, setUserKit] = useState([]);
  const [requireMedical, setRequireMedical] = useState(false);

  useEffect(() => {
    loadDataAndGenerate();
  }, []);

  const loadDataAndGenerate = async () => {
    // Load data from localStorage
    const savedNeed = localStorage.getItem('acupedia_selected_need');
    const savedKit = localStorage.getItem('acupedia_kit');
    const savedProfile = localStorage.getItem('acupedia_profile');
    const savedPreferCold = localStorage.getItem('acupedia_prefer_cold');

    if (!savedNeed) {
      toast.error('Te rugăm să selectezi o nevoie din pagina principală');
      navigate('/');
      return;
    }

    if (!savedKit || JSON.parse(savedKit).length === 0) {
      toast.error('Te rugăm să configurezi trusa de plante');
      navigate('/kit');
      return;
    }

    const need = JSON.parse(savedNeed);
    const kit = JSON.parse(savedKit);
    const profile = savedProfile ? JSON.parse(savedProfile) : {};
    const preferCold = savedPreferCold ? JSON.parse(savedPreferCold) : false;

    setSelectedNeed(need);
    setUserKit(kit);

    await generateRecipes(need, kit, profile, preferCold);
  };

  const generateRecipes = async (need, kit, profile, preferCold) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/recipes/generate`, {
        need_id: need.id,
        user_kit: kit,
        profile: profile,
        prefer_cold: preferCold
      });

      setRecipes(response.data.recipes || []);
      setWarnings(response.data.warnings || []);
      setDisclaimer(response.data.disclaimer || '');
      setRequireMedical(response.data.require_medical || false);

      if (response.data.recipes?.length === 0) {
        toast.warning('Nu am putut genera rețete cu plantele disponibile în kit');
      }
    } catch (error) {
      console.error('Error generating recipes:', error);
      toast.error('Eroare la generarea rețetelor');
    } finally {
      setLoading(false);
    }
  };

  const handleRecipeClick = (recipe) => {
    localStorage.setItem('acupedia_current_recipe', JSON.stringify(recipe));
    navigate(`/recipe/${recipe.id}`);
  };

  const handleRegenerate = () => {
    const savedNeed = localStorage.getItem('acupedia_selected_need');
    const savedKit = localStorage.getItem('acupedia_kit');
    const savedProfile = localStorage.getItem('acupedia_profile');
    const savedPreferCold = localStorage.getItem('acupedia_prefer_cold');

    if (savedNeed && savedKit) {
      generateRecipes(
        JSON.parse(savedNeed),
        JSON.parse(savedKit),
        savedProfile ? JSON.parse(savedProfile) : {},
        savedPreferCold ? JSON.parse(savedPreferCold) : false
      );
    }
  };

  return (
    <div className="py-8 md:py-12 animate-fade-in">
      <div className="container-acupedia">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-4 -ml-2"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi la nevoi
            </Button>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-acupedia-teal flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h1 className="section-header mb-0">Rețete Recomandate</h1>
            </div>
            {selectedNeed && (
              <p className="section-subheader">
                Pentru: <strong className="text-acupedia-teal">{selectedNeed.name_ro}</strong>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-4 py-2 text-sm">
              <Package className="w-4 h-4 mr-2" />
              {userKit.length} plante în kit
            </Badge>
            <Button
              variant="outline"
              onClick={handleRegenerate}
              disabled={loading}
              className="btn-ghost"
              data-testid="regenerate-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Regenerează
            </Button>
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="mb-8 space-y-3">
            {warnings.map((warning, index) => (
              <div key={index} className="medical-disclaimer">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{warning}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Require Medical Consultation */}
        {requireMedical && (
          <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-heading text-lg text-red-800 mb-2">Consultație medicală necesară</h3>
                <p className="text-sm text-red-700">
                  Pe baza profilului tău, recomandăm consultarea unui medic înainte de a folosi ceaiuri terapeutice.
                  Acest lucru este deosebit de important pentru copii, persoane însărcinate sau cu afecțiuni cronice.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="skeleton h-48 rounded-xl"></div>
            ))}
          </div>
        ) : recipes.length > 0 ? (
          /* Recipe Cards */
          <div className="space-y-6">
            {recipes.map((recipe, index) => (
              <div
                key={recipe.id}
                className="card-recipe cursor-pointer hover:shadow-lg transition-all duration-300 group"
                onClick={() => handleRecipeClick(recipe)}
                data-testid={`recipe-card-${index}`}
              >
                {/* Match Score Badge */}
                <div className="absolute top-4 right-4">
                  <Badge className={`${
                    recipe.match_score >= 70 ? 'bg-green-100 text-green-800' :
                    recipe.match_score >= 50 ? 'bg-amber-100 text-amber-800' :
                    'bg-stone-100 text-stone-700'
                  }`}>
                    {recipe.match_score}% potrivire
                  </Badge>
                </div>

                {/* Header */}
                <div className="mb-6">
                  <h2 className="font-heading text-2xl text-acupedia-teal mb-2 group-hover:text-acupedia-teal-light transition-colors">
                    {recipe.name}
                  </h2>
                  <p className="text-stone-600">{recipe.scop}</p>
                </div>

                {/* Ingredients Preview */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide mb-3">
                    Ingrediente
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {recipe.ingredients.map((ing, i) => (
                      <span 
                        key={i}
                        className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-stone-200"
                      >
                        <Leaf className="w-4 h-4 text-acupedia-teal" />
                        <span className="font-medium text-stone-800">{ing.herb_name}</span>
                        <span className="font-mono text-sm text-acupedia-teal">{ing.grams}g</span>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Quick Info */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Thermometer className="w-4 h-4 text-acupedia-gold" />
                    <span>{recipe.temperature}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Activity className="w-4 h-4 text-acupedia-gold" />
                    <span>{recipe.infusion_time_min} min infuzie</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <span className="font-mono">{recipe.water_ml} ml</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <span>{recipe.infusion_type}</span>
                  </div>
                </div>

                {/* Meridians */}
                {recipe.meridians?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {recipe.meridians.map((meridian, i) => (
                      <span key={i} className="meridian-badge">
                        {meridian}
                      </span>
                    ))}
                  </div>
                )}

                {/* CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                  <span className="text-sm text-stone-500">
                    {recipe.cald_rece_recommendation?.split(' ')[0]}
                  </span>
                  <span className="flex items-center gap-2 text-acupedia-teal font-medium group-hover:gap-3 transition-all">
                    Vezi rețeta completă
                    <ChevronRight className="w-5 h-5" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* No Recipes */
          <div className="text-center py-16 bg-stone-50 rounded-xl">
            <Leaf className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-stone-600 mb-2">
              Nu am găsit rețete potrivite
            </h3>
            <p className="text-stone-500 mb-6 max-w-md mx-auto">
              Încearcă să adaugi mai multe plante în kit sau să selectezi o altă nevoie.
            </p>
            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/kit')}
                data-testid="go-to-kit-btn"
              >
                <Package className="w-4 h-4 mr-2" />
                Editează Trusa
              </Button>
              <Button
                onClick={() => navigate('/')}
                className="btn-primary"
                data-testid="go-home-btn"
              >
                Alege altă nevoie
              </Button>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {disclaimer && (
          <div className="medical-disclaimer mt-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-acupedia-gold flex-shrink-0 mt-0.5" />
              <p className="text-sm">{disclaimer}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
